import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  IconButton,
  Alert,
} from '@mui/material';
import {
  AccountCircle,
  PersonAddAlt1,
  PersonRemove,
  GroupRemove,
  ExitToApp,
  Close,
  ErrorOutline,
} from '@mui/icons-material';
import {
  promoteGroupMember,
  demoteGroupOwner,
  removeGroupMember,
} from '../../graphql/mutations';
import { getAllUsersInGroup } from '../../graphql/queries';
import { API } from '../../services/amplifyCompat';
import { useNavigate } from 'react-router-dom';
import LeaveGroupDialogContent from './LeaveGroupDialogContent';
import { MemberDialogAction } from './MemberDialogAction';

import useTranslation from '../customHooks/translations';

const GroupMemberDialog = ({
  selectedMember,
  openDialog,
  handleClose,
  groupInfo,
  groupMembers,
  setGroupMembers,
  currentUserOwner,
  cognitoUser,
}) => {
  const dialogDisplayInitial = {
    promoteUserSuccess: false,
    demoteOwnerSuccess: false,
    removeUserSuccess: false,
    leaveGroupWarning: false,
    demoteWarning: false,
  };
  const [dialogDisplay, setDialogDisplay] = useState(dialogDisplayInitial);
  const [actionError, setActionError] = useState('');
  const userType = cognitoUser && cognitoUser.attributes['custom:type'];
  const navigate = useNavigate();
  const translation = useTranslation();

  const refetchMembers = async () => {
    try {
      const res = await API.graphql({
        query: getAllUsersInGroup,
        variables: { group_id: groupInfo.group_id },
      });
      setGroupMembers(res.data.getAllUsersInGroup);
    } catch (err) {
      console.log(err);
    }
  };

  const promoteUser = async () => {
    setActionError('');
    try {
      await API.graphql({
        query: promoteGroupMember,
        variables: {
          group_id: groupInfo.group_id,
          user_id: selectedMember.user_id,
        },
      });
      setDialogDisplay({ ...dialogDisplay, promoteUserSuccess: true });
      await refetchMembers();
    } catch (err) {
      console.log(err);
      setActionError(
        translation.createStudentAccount?.unexpectedError ||
          'An unexpected error occurred. Please try again later.'
      );
    }
  };

  const demoteOwner = async () => {
    setActionError('');
    if (
      groupMembers.filter((member) => member.user_role === 'owner').length === 1
    ) {
      setDialogDisplay({ ...dialogDisplay, demoteWarning: true });
    } else {
      try {
        await API.graphql({
          query: demoteGroupOwner,
          variables: {
            group_id: groupInfo.group_id,
            user_id: selectedMember.user_id,
          },
        });
        setDialogDisplay({ ...dialogDisplay, demoteOwnerSuccess: true });
        await refetchMembers();
      } catch (err) {
        console.log(err);
        setActionError(
          translation.createStudentAccount?.unexpectedError ||
            'An unexpected error occurred. Please try again later.'
        );
      }
    }
  };

  const removeUser = async () => {
    setActionError('');
    //check if leaving user is the only owner
    const allOwners =
      groupMembers &&
      groupMembers.filter((member) => member.user_role === 'owner');
    const isOnlyOwner =
      allOwners.length === 1 &&
      allOwners.some((owner) => owner.user_id === selectedMember.user_id);
    //if there is only 1 group member, or if the user being removed is the only owner, show warning
    if (groupMembers.length === 1 || isOnlyOwner) {
      setDialogDisplay({ ...dialogDisplay, leaveGroupWarning: true });
    } else {
      try {
        await API.graphql({
          query: removeGroupMember,
          variables: {
            group_id: groupInfo.group_id,
            user_id: selectedMember.user_id,
          },
        });
        setDialogDisplay({ ...dialogDisplay, removeUserSuccess: true });
        await refetchMembers();
      } catch (err) {
        console.log(err);
        setActionError(
          translation.createStudentAccount?.unexpectedError ||
            'An unexpected error occurred. Please try again later.'
        );
      }
    }
  };

  //display promote, demote and remove options only if current user is a group owner
  const renderOwnerAdminView = () => {
    return (
      (currentUserOwner || userType === 'Admin') && (
        <>
          {selectedMember.user_role === 'member' ? (
            <MemberDialogAction
              onSelectAction={promoteUser}
              actionLabel={translation.promoteUserToOwner}
              actionIcon={PersonAddAlt1}
            />
          ) : (
            <MemberDialogAction
              onSelectAction={demoteOwner}
              actionLabel={translation.demoteUserToMember}
              actionIcon={PersonRemove}
            />
          )}
          <MemberDialogAction
            onSelectAction={removeUser}
            actionLabel={translation.removeUserFromGroup}
            actionIcon={GroupRemove}
          />
        </>
      )
    );
  };

  const onDialogClose = () => {
    setActionError('');
    setDialogDisplay(dialogDisplayInitial);
    handleClose();
  };

  return (
    <>
      {openDialog && (
        <Dialog
          aria-labelledby="already-member-dialog"
          PaperProps={{
            sx: {
              p: '1em',
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
              alignItems: 'center',
              minWidth: '30%',
            },
          }}
          open={openDialog}
          onClose={onDialogClose}
        >
          <IconButton sx={{ alignSelf: 'flex-end' }} onClick={onDialogClose}>
            <Close />
          </IconButton>
          {actionError && (
            <Alert
              severity="error"
              onClose={() => setActionError('')}
              sx={{ mb: '1em', width: '100%' }}
            >
              {actionError}
            </Alert>
          )}
          {/* if current cognito user is the selected member, give option to leave group */}
          {!dialogDisplay.leaveGroupWarning &&
          selectedMember.user_id ===
            Number(cognitoUser.attributes['custom:id']) ? (
            <>
              <DialogTitle>{selectedMember.name}</DialogTitle>
              <DialogContent>
                {translation.yourRoleColon}{' '}
                {selectedMember.user_role.charAt(0).toUpperCase() +
                  selectedMember.user_role.slice(1)}
              </DialogContent>
              <List sx={{ pt: '1em', pb: '2em' }}>
                <MemberDialogAction
                  onSelectAction={() =>
                    setDialogDisplay({
                      ...dialogDisplay,
                      leaveGroupWarning: true,
                    })
                  }
                  actionLabel={translation.leaveGroup}
                  actionIcon={ExitToApp}
                />
              </List>
            </>
          ) : (
            /* if there are no success messages to show, display the initial dialog menu */
            Object.values(dialogDisplay).every((value) => value === false) && (
              <>
                <DialogTitle>{selectedMember.name}</DialogTitle>
                <DialogContent>
                  {translation.roleColon}{' '}
                  {selectedMember.user_role.charAt(0).toUpperCase() +
                    selectedMember.user_role.slice(1)}
                </DialogContent>
                <List sx={{ pt: '1em', pb: '2em' }}>
                  <MemberDialogAction
                    onSelectAction={() =>
                      navigate(`/user-profile/${selectedMember.user_id}`)
                    }
                    actionLabel={translation.viewUserProfile}
                    actionIcon={AccountCircle}
                  />
                  {renderOwnerAdminView()}
                </List>
              </>
            )
          )}
          {dialogDisplay.promoteUserSuccess && (
            <>
              <DialogTitle>{translation.success}</DialogTitle>
              <DialogContent>
                {translation.formatString(translation.successUserNowOwner, selectedMember.name)}
              </DialogContent>
            </>
          )}
          {dialogDisplay.demoteOwnerSuccess && (
            <>
              <DialogTitle>{translation.success}</DialogTitle>
              <DialogContent>
                {translation.formatString(translation.successUserNowMember, selectedMember.name)}
              </DialogContent>
            </>
          )}
          {dialogDisplay.removeUserSuccess && (
            <>
              <DialogTitle>{translation.success}</DialogTitle>
              <DialogContent>
                {translation.formatString(translation.successUserNowRemoved, selectedMember.name)}
              </DialogContent>
            </>
          )}
          {dialogDisplay.demoteWarning && (
            <>
              <DialogTitle>{translation.demotionErrorTitle}</DialogTitle>
              <ErrorOutline fontSize="large" />
              <DialogContent>
                {translation.demotionErrorText}
              </DialogContent>
            </>
          )}

          {dialogDisplay.leaveGroupWarning && (
            <LeaveGroupDialogContent
              handleClose={() =>
                setDialogDisplay({
                  ...dialogDisplay,
                  leaveGroupWarning: false,
                })
              }
              groupInfo={groupInfo}
              groupMembers={groupMembers}
              currentUserOwner={currentUserOwner}
              userId={selectedMember.user_id}
              cognitoUser={cognitoUser}
            />
          )}
        </Dialog>
      )}
    </>
  );
};

export default GroupMemberDialog;
